import { ConvexBetterAuthProvider } from '@convex-dev/better-auth/react'
import { ConvexReactClient } from 'convex/react'
import { useState, type ReactNode } from 'react'
import { Toaster } from 'react-hot-toast'

import { AuthStateProvider, StaticAuthProvider } from '@/lib/auth/auth-context'
import { authClient } from '@/lib/auth/client'
import { PUBLIC_CONVEX_URL } from '@/lib/env'
import { logClientError } from '@/lib/logger'
import { useHydrated } from '@/lib/use-hydrated'

let convexSingleton: ConvexReactClient | null = null

function getConvexClient(): ConvexReactClient | null {
  if (typeof window === 'undefined') {
    // SSR/SSG: skip Convex; marketing pages render from static catalog data.
    return null
  }
  if (!convexSingleton) {
    if (!PUBLIC_CONVEX_URL) {
      logClientError('VITE_CONVEX_URL is not set; Convex queries will not run.')
      return null
    }
    convexSingleton = new ConvexReactClient(PUBLIC_CONVEX_URL)
  }
  return convexSingleton
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Lazy initializer runs once on the client, returning null on the server.
  const [convex] = useState<ConvexReactClient | null>(() => getConvexClient())

  // The Toaster renders DOM (a fixed container) that the prerendered/SSG HTML
  // never contains, since SSG runs the `!convex` branch below with no Toaster.
  // Rendering it on the client's first paint therefore breaks hydration ("server
  // HTML didn't match the client") — which can blank prerendered pages. Gate it
  // until hydrated so the first client render matches the server exactly, then
  // mount the Toaster. The Convex provider itself adds no DOM, so it's
  // hydration-safe and can stay from the first render.
  const hydrated = useHydrated()

  // HelmetProvider is supplied by vite-react-ssg at the app root.
  // During SSG/prerender we render marketing pages without the Convex client —
  // they only rely on static data in @/data/*. Convex-backed pages mount on the
  // client after hydration. StaticAuthProvider keeps useAuth() resolving to
  // 'loading' here so prerendered protected routes render a stable loader (and
  // hydrate without mismatch) instead of throwing for the missing provider.
  if (!convex) {
    return <StaticAuthProvider>{children}</StaticAuthProvider>
  }

  return (
    <ConvexBetterAuthProvider client={convex} authClient={authClient}>
      <AuthStateProvider>
        {children}
        {hydrated ? <Toaster position="top-right" gutter={10} /> : null}
      </AuthStateProvider>
    </ConvexBetterAuthProvider>
  )
}
